import styled from '@emotion/styled';
import { Box, DialogActions, Fade, Stack, Typography } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { AxiosError, AxiosResponse } from 'axios';
import { ActionsContainer } from 'klayowebshared';
import { concat, isEmpty, uniqBy } from 'lodash';
import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../common/AppContext';
import { Dialog } from '../../components/dialogs/Dialog';
import { Snackbar } from '../../components/Snackbar';
import { StandardLibraryTable } from '../../components/table/StandardLibraryTable';
import { TablePlaceholder } from '../../components/TablePlaceholder';
import { useStandardsLibrary } from '../../contexts/StandardsLibraryContext';
import { useAppMutation } from '../../hooks/useAppMutation';
import {
  ICompetency,
  ICompetencyImportResponse,
  StandardLibraryCompetency,
} from '../../interfaces/Competency';
import { ITask, ITaskImportResponse } from '../../interfaces/Task';
import { useLocale } from '../../providers/LocaleProvider';
import { useSnackbar } from '../../providers/SnackbarProvider';
import AttributeIcons from '../../resources/images/icons-attribute.svg';
import ChecklistIcons from '../../resources/images/icons-checklist.svg';
import RemoveIcons from '../../resources/images/icons-remove.svg';
import { KLAYO_COLORS } from '../../themes';
import ImportCompetenciesDialog from './ImportCompetenciesDialog';
import ImportCompeteniesErrorDialog from './ImportCompeteniesErrorDialog';
import ImportTaskDetailDialog from './ImportTaskDetailDialog';
import ImportTasksErrorDialog from './ImportTasksErrorDialog';
import LimitedOverlay from './LimitedOverlay';

export type StandardMatchedTasksAndCompetenciesProps = {
  theme: any;
  tasks: ITask[];
  competencies: Omit<StandardLibraryCompetency, 'hasTraining'>[];
  onClose?: () => void;
  hideActions?: boolean;
  onSelectionChange?: (hasSelection: boolean, taskCount: number, competencyCount: number) => void;
  /** Bump to remount the two DataGrid tables (recovers their column layout after the host hid them
   *  with display:none). Selection lives in this component and is passed as a controlled
   *  rowSelectionModel, so remounting the tables preserves it. */
  tableNonce?: number;
};
export type StandardMatchedTasksAndCompetenciesRef = {
  getSelectedTaskIds: () => GridRowSelectionModel;
  getSelectedCompetencyIds: () => GridRowSelectionModel;
  triggerImport: () => Promise<void>;
  isImportDisabled: () => boolean;
};

const StandardMatchedTasksAndCompetencies = forwardRef<
  StandardMatchedTasksAndCompetenciesRef,
  StandardMatchedTasksAndCompetenciesProps
>(({ theme, tasks, competencies, onClose, hideActions, onSelectionChange, tableNonce }, ref) => {
  const appContext = useContext(AppContext);

  const [selectedTasks, setSelectedTasks] = useState<GridRowSelectionModel>([]);
  const [selectedCompetencies, setSelectedCompetencies] = useState<GridRowSelectionModel>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedCompetencyId, setSelectedCompetencyId] = useState<string | null>(null);
  const [showRequiredDialog, setShowRequiredDialog] = useState(false);
  const [requiredTasks, setRequiredTasks] = useState<ITask[]>([]);
  const [errorCompetencies, setErrorCompetencies] = useState<ICompetency[]>([]);
  const [showImportCompetenciesError, setShowImportCompetenciesError] = useState(false);
  const [showNotImportedCompetencies, setShowNotImportedCompetencies] = useState(false);
  const [showNotImportedTasks, setShowNotImportedTasks] = useState(false);
  const [errorTasks, setErrorTasks] = useState<ITask[]>([]);
  const [showImportTasksError, setShowImportTasksError] = useState(false);

  const { showSnackbar } = useSnackbar();
  const { translate } = useLocale();
  const navigate = useNavigate();
  const { isTrial } = useStandardsLibrary();

  const { mutateAsync: onImportTasks, isPending: isImportTasksLoading } = useAppMutation<
    AxiosResponse<ITaskImportResponse>,
    unknown,
    string[]
  >('post', '/StandardLibrary/tasks/import');

  const { mutateAsync: onImportCompetencies, isPending: isImportCompetenciesLoading } =
    useAppMutation<AxiosResponse<ICompetencyImportResponse>, AxiosError<any>, string[]>(
      'post',
      '/StandardLibrary/competencies/import'
    );

  const extraTasks = useMemo(() => tasks.filter((t) => t.isExtra), [tasks]);
  const [noExtraCompetencies, extraCompetencies] = useMemo(
    () =>
      competencies.reduce<[typeof competencies, typeof competencies]>(
        ([noExtra, extra], c) => {
          c.isExtra ? extra.push(c) : noExtra.push(c);
          return [noExtra, extra];
        },
        [[], []]
      ),
    [competencies]
  );

  // Get all competencies including those from selected tasks
  const allCompetencies = useMemo(() => {
    const taskCompetencies = selectedTasks.flatMap((taskId) => {
      const task = tasks.find((t) => t.id === taskId);
      return task?.taskCompetencies || [];
    });

    // Convert task competencies to StandardLibraryCompetency format
    const convertedTaskCompetencies: Omit<StandardLibraryCompetency, 'hasTraining'>[] =
      taskCompetencies.map((tc) => ({
        id: tc.id,
        name: tc.name,
        hasExpiryDate: tc.hasExpiryDate,
        competencyCategories: tc.competencyCategories,
        description: '',
        trainings: [],
        score: tc.score || 0,
        isExtra: tc.isExtra,
      }));

    // Combine and remove duplicates
    return uniqBy(
      [...noExtraCompetencies, ...convertedTaskCompetencies, ...extraCompetencies],
      'id'
    );
  }, [noExtraCompetencies, extraCompetencies, tasks, selectedTasks]);

  const onSelectedTask = async (_: any, row: any) => {
    setSelectedTaskId(row.id);
  };

  const onSelectedCompetency = (_: any, row: any) => {
    !row.isExtra && setSelectedCompetencyId(row.id);
  };

  const onCloseTaskDetail = () => {
    setSelectedTaskId(null);
  };

  const onCloseCompetencyDetail = () => {
    setSelectedCompetencyId(null);
  };

  const onImportTasksAndCompetencies = async () => {
    if (isEmpty(selectedTasks) && isEmpty(selectedCompetencies)) {
      return;
    }

    const searchParams = new URLSearchParams();

    try {
      if (!isEmpty(selectedCompetencies)) {
        const importCompetencyRes = await onImportCompetencies(selectedCompetencies as string[]);

        const { notImportedCompetencies, importedCompetencies, alreadyExistsCompetencies } =
          importCompetencyRes.data;

        if (!isEmpty(notImportedCompetencies)) {
          setErrorCompetencies(notImportedCompetencies);
          setShowNotImportedCompetencies(true);

          return;
        }

        const competencyIds = concat(importedCompetencies, alreadyExistsCompetencies).map(
          (competency) => competency.attributeDefinitionId
        );

        searchParams.set('competencyIds', competencyIds.join(','));
      }

      if (!isEmpty(selectedTasks)) {
        // import tasks
        const importTaskRes = await onImportTasks(selectedTasks as string[]);

        const { failedToImportTasks, importedTasks, notImportedTasks } = importTaskRes.data;

        if (failedToImportTasks && !isEmpty(failedToImportTasks)) {
          setShowNotImportedTasks(true);
          setErrorTasks(failedToImportTasks);
          return;
        }

        const taskIds = concat(importedTasks, notImportedTasks).map((task) => task.id);

        searchParams.set('taskIds', taskIds.join(','));
      }

      navigate(`/settings/jobs/new?${searchParams.toString()}`);
      onClose?.();
    } catch (error) {
      if (error instanceof AxiosError) {
        showSnackbar(
          error.response?.data.message || translate('messages.competenciesImportError'),
          'error'
        );
      }
    }
  };

  useImperativeHandle(ref, () => ({
    getSelectedTaskIds: () => selectedTasks,
    getSelectedCompetencyIds: () => selectedCompetencies,
    triggerImport: onImportTasksAndCompetencies,
    isImportDisabled: () => isEmpty(selectedTasks) && isEmpty(selectedCompetencies),
  }));

  useEffect(() => {
    onSelectionChange?.(
      !isEmpty(selectedTasks) || !isEmpty(selectedCompetencies),
      selectedTasks.length,
      selectedCompetencies.length
    );
  }, [selectedTasks, selectedCompetencies]);

  const handleTaskSelectionChange = async (newSelection: GridRowSelectionModel) => {
    setSelectedTasks(newSelection);

    // Get competencies from newly selected tasks
    const newTaskCompetencies = newSelection.flatMap((taskId) => {
      const task = tasks.find((t) => t.id === taskId);
      return task?.taskCompetencies?.map((tc) => tc.id) || [];
    });

    // Get competencies from previously selected tasks
    const oldTaskCompetencies = selectedTasks.flatMap((taskId) => {
      const task = tasks.find((t) => t.id === taskId);
      return task?.taskCompetencies?.map((tc) => tc.id) || [];
    });

    // Get competencies that were removed (in old but not in new)
    const removedCompetencies = oldTaskCompetencies.filter(
      (id) => !newTaskCompetencies.includes(id)
    );

    // Update selected competencies
    const updatedSelection = selectedCompetencies
      .map((id) => id.toString())
      .filter((id) => !removedCompetencies.includes(id));

    // Add competencies from newly selected tasks
    const finalSelection = Array.from(new Set([...updatedSelection, ...newTaskCompetencies]));

    setSelectedCompetencies(finalSelection);
  };

  const handleCompetencySelectionChange = (newSelection: GridRowSelectionModel) => {
    // Find competencies that are being deselected
    const deselectedCompetencies = selectedCompetencies.filter((id) => !newSelection.includes(id));

    // For each deselected competency, check if it's required by any selected task
    const requiredTasksForDeselected = uniqBy(
      deselectedCompetencies.flatMap((compId) => {
        return selectedTasks
          .map((taskId) => tasks.find((t) => t.id === taskId))
          .filter((task) => task?.taskCompetencies?.some((tc) => tc.id === compId)) as ITask[];
      }),
      'id'
    );

    if (requiredTasksForDeselected.length > 0) {
      // Show dialog with required tasks
      setRequiredTasks(requiredTasksForDeselected);
      setShowRequiredDialog(true);
      return;
    }

    // If no required competencies are being deselected, update the selection
    setSelectedCompetencies(newSelection);
  };

  const isShowLimitedCompetencyOverlay = useMemo(
    () => isTrial && allCompetencies.some((c) => c.isExtra),
    [isTrial, allCompetencies]
  );
  const isShowLimitedTaskOverlay = useMemo(
    () => isTrial && tasks.some((t) => t.isExtra),
    [isTrial, tasks]
  );

  return (
    <>
      {isEmpty(tasks) && isEmpty(allCompetencies) ? (
        <TablePlaceholder
          text={{
            primary: translate('standardsLibrary.noTasksOrCompetencies'),
            secondary: translate('standardsLibrary.noTasksOrCompetenciesDesc'),
          }}
          icon={<AttributeIcons />}
        />
      ) : (
        <Box display='flex' flexDirection={'column'} gap={4}>
          <Box display='flex' flexDirection={'column'} gap={3} position={'relative'}>
            <SectionTitle variant='h6'>{translate('standardsLibrary.tasksSection')}</SectionTitle>
            <StandardLibraryTable
              key={`tasks-${tableNonce ?? 0}`}
              theme={theme}
              columns={[
                {
                  type: StandardLibraryTable.columns.name,
                  label: translate('entities.task'),
                  sortable: false,
                },
              ]}
              rows={tasks}
              isLoading={false}
              dense={true}
              paper={false}
              onJobSelect={onSelectedTask}
              rowSelectionModel={selectedTasks}
              onRowSelectionModelChange={handleTaskSelectionChange}
              pagination={false}
              showFooter={false}
              checkboxSelection
              selectable={false}
              isRowSelectable={(data: any) => {
                return !data.row.isExtra;
              }}
              sx={{
                '& .MuiDataGrid-row': { cursor: 'pointer !important' },
                '& .MuiDataGrid-columnHeader:focus-within': {
                  outline: 'none',
                },
              }}
              noResultsComponent={
                <TablePlaceholder
                  text={{
                    primary: translate('standardsLibrary.noMatchingTasks'),
                    secondary: translate('standardsLibrary.noMatchingTasksDesc'),
                  }}
                  icon={<ChecklistIcons />}
                />
              }
              noRowsComponent={
                <TablePlaceholder
                  text={{
                    primary: translate('standardsLibrary.noMatchingTasks'),
                    secondary: translate('standardsLibrary.noMatchingTasksDesc'),
                  }}
                  icon={<ChecklistIcons />}
                />
              }
            />
            {isShowLimitedTaskOverlay && (
              <LimitedOverlay
                isCreatePortal={false}
                height={extraTasks.length * 56}
                translate={translate}
              />
            )}
          </Box>
          <Box display='flex' flexDirection={'column'} gap={3} position={'relative'}>
            <SectionTitle variant='h6'>
              {translate('standardsLibrary.competenciesSection')}
            </SectionTitle>
            <StandardLibraryTable
              key={`competencies-${tableNonce ?? 0}`}
              theme={theme}
              columns={[
                {
                  type: StandardLibraryTable.columns.name,
                  label: translate('entities.competency'),
                  sortable: false,
                },
              ]}
              rows={allCompetencies}
              isLoading={false}
              dense={true}
              paper={false}
              onJobSelect={onSelectedCompetency}
              rowSelectionModel={selectedCompetencies}
              onRowSelectionModelChange={handleCompetencySelectionChange}
              pagination={false}
              showFooter={false}
              checkboxSelection
              selectable={false}
              isRowSelectable={(data: any) => {
                return !data.row.isExtra;
              }}
              sx={{
                '& .MuiDataGrid-row': { cursor: 'pointer !important' },
                '& .MuiDataGrid-columnHeader:focus-within': {
                  outline: 'none',
                },
              }}
              noResultsComponent={
                <TablePlaceholder
                  text={{
                    primary: translate('standardsLibrary.noMatchingCompetencies'),
                    secondary: translate('standardsLibrary.noMatchingCompetenciesDesc'),
                  }}
                  icon={<AttributeIcons />}
                />
              }
              noRowsComponent={
                <TablePlaceholder
                  text={{
                    primary: translate('standardsLibrary.noMatchingCompetencies'),
                    secondary: translate('standardsLibrary.noMatchingCompetenciesDesc'),
                  }}
                  icon={<AttributeIcons />}
                />
              }
            />
            {isShowLimitedCompetencyOverlay && (
              <LimitedOverlay
                isCreatePortal={false}
                height={extraCompetencies.length * 56}
                translate={translate}
              />
            )}
          </Box>
        </Box>
      )}
      {!hideActions && (
        <DialogActions
          sx={{
            padding: { xs: '24px 0', md: '40px 0' },
          }}
        >
          <ActionsContainer
            actions={[
              {
                label: translate('standardsLibrary.importAndContinue'),
                primary: true,
                variant: 'filled',
                onClick: onImportTasksAndCompetencies,
                disabled:
                  isImportCompetenciesLoading ||
                  isImportTasksLoading ||
                  (isEmpty(selectedTasks) && isEmpty(selectedCompetencies)),
                isLoading: isImportCompetenciesLoading || isImportTasksLoading,
              },
              {
                label: translate('common.cancel'),
                onClick: onClose,
                disabled: isImportCompetenciesLoading || isImportTasksLoading,
              },
            ]}
            justifyActions='right'
            onClose={onClose}
          />
        </DialogActions>
      )}
      <ImportTaskDetailDialog
        theme={theme}
        taskId={selectedTaskId}
        onClose={onCloseTaskDetail}
        open={!!selectedTaskId}
        isImport={false}
      />
      <ImportCompetenciesDialog
        theme={theme}
        competencyId={selectedCompetencyId}
        onClose={onCloseCompetencyDetail}
        open={!!selectedCompetencyId}
        isImport={false}
      />
      <Dialog
        open={showRequiredDialog}
        theme={theme}
        onClose={() => setShowRequiredDialog(false)}
        title={translate('settings.cannotRemoveCompetency')}
        actions={[
          {
            label: translate('common.cancel'),
            onClick: () => setShowRequiredDialog(false),
          },
        ]}
      >
        <Box mb={2}>
          <Typography>
            {translate('settingsViews.jobBenchmarking.competencyRequiredByTasks')}
          </Typography>
        </Box>
        <Box>
          {requiredTasks.map((task) => (
            <Typography
              variant='body1'
              sx={{
                color: KLAYO_COLORS.GreyGR500,
                fontWeight: 600,
                fontSize: '.9rem',
              }}
              key={task.id}
            >
              {task.name}
            </Typography>
          ))}
        </Box>
      </Dialog>

      <Fade in={showNotImportedCompetencies} unmountOnExit>
        <div>
          <Snackbar
            open={true}
            duration={null}
            type={'error'}
            onClose={() => setShowNotImportedCompetencies(false)}
            message={
              <>
                <Typography hidden={errorCompetencies.length === 0}>
                  {translate(
                    errorCompetencies.length === 1
                      ? 'settingsViews.competencies.importCompetencies.competencyNotImported'
                      : 'settingsViews.competencies.importCompetencies.competenciesNotImported',
                    { count: errorCompetencies.length }
                  )}
                </Typography>
              </>
            }
            action={
              <>
                <div
                  style={{
                    marginLeft: 'auto',
                    marginRight: '0.5rem',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                  hidden={errorCompetencies.length === 0}
                  className='klayo-snackbar_text'
                  onClick={() => {
                    setShowImportCompetenciesError(true);
                    setShowNotImportedCompetencies(false);
                  }}
                >
                  {translate('common.viewDetails')}
                </div>
                <BlackIconContainer
                  style={{
                    padding: '0',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                  onClick={() => {
                    setShowNotImportedCompetencies(false);
                  }}
                  className='klayo-snackbar_text '
                >
                  <RemoveIcons />
                </BlackIconContainer>
              </>
            }
          />
        </div>
      </Fade>

      <Fade in={showNotImportedTasks} unmountOnExit>
        <div>
          <Snackbar
            open={true}
            duration={null}
            type={'error'}
            onClose={() => setShowNotImportedTasks(false)}
            message={
              <>
                <Typography>
                  {translate(
                    errorTasks.length === 1
                      ? 'settingsViews.tasks.importTasks.taskNotImported'
                      : 'settingsViews.tasks.importTasks.tasksNotImported',
                    { count: errorTasks.length }
                  )}
                </Typography>
              </>
            }
            action={
              <>
                <div
                  style={{
                    marginLeft: 'auto',
                    marginRight: '0.5rem',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                  hidden={errorTasks.length === 0}
                  className='klayo-snackbar_text'
                  onClick={() => {
                    setShowImportTasksError(true);
                    setShowNotImportedTasks(false);
                  }}
                >
                  {translate('common.viewDetails')}
                </div>
                <BlackIconContainer
                  style={{
                    padding: '0',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                  onClick={() => {
                    setShowNotImportedTasks(false);
                  }}
                  className='klayo-snackbar_text '
                >
                  <RemoveIcons />
                </BlackIconContainer>
              </>
            }
          />
        </div>
      </Fade>

      <ImportCompeteniesErrorDialog
        theme={theme}
        open={showImportCompetenciesError}
        onClose={() => {
          setShowImportCompetenciesError(false);
          setErrorCompetencies([]);
        }}
        competencies={errorCompetencies}
        description={`
            The following ${errorCompetencies.length > 1 ? 'competencies' : 'competency'} could not be imported because its mapped training recurrence does not match:
        `}
      />

      <ImportTasksErrorDialog
        theme={theme}
        open={showImportTasksError}
        onClose={() => {
          setShowImportTasksError(false);
          setErrorTasks([]);
        }}
        tasks={errorTasks}
        description={`The following ${`${errorTasks.length > 1 ? 'tasks' : 'task'}`} could not be imported because 
        ${`${errorTasks.length > 1 ? 'they' : 'it'}`} already 
        ${`${errorTasks.length > 1 ? 'exist' : 'exists'}`}`}
      />
    </>
  );
});

StandardMatchedTasksAndCompetencies.displayName = 'StandardMatchedTasksAndCompetencies';

export default StandardMatchedTasksAndCompetencies;

const SectionTitle = styled(Typography)`
  font-size: 1.025rem;
  font-weight: 600;
`;

const SelectAllText = styled(Stack)`
  color: ${KLAYO_COLORS.NeonBlueNB500};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  width: fit-content;
`;

const BlackIconContainer = styled.div`
  svg {
    path[fill='#1B3DF2'] {
      fill: #000;
    }
  }
`;
